import React, { ReactNode } from 'react';
import { StyleProp, StyleSheet, TextStyle, View, useWindowDimensions } from 'react-native';
import Animated from 'react-native-reanimated';
import { Edges, SafeAreaView } from 'react-native-safe-area-context';

import {
    MaterialTopTabBar,
    MaterialTopTabBarProps,
    MaterialTopTabNavigationOptions,
} from '@react-navigation/material-top-tabs';

import { ds } from '@/components/lib/design.system';
import { common, designSystem } from '@/styles/styles';

import { LineSeparator, LineSeparatorStyle } from './LineSeparator';
import { Text } from './TextComponent';
import { AppColors } from './lib/theme';

/**
 * Use this component with TOP_TAB_BAR_CONFIG and createMaterialTopTabNavigator to create a basic TopTabBar navigator
 * @param {MaterialTopTabBarProps & { headerComponent: ReactNode }} accepts all tab bar props and an additional
 * `headerComponent` that allows inject a component above the tab bar
 */

export const TAB_HEIGHT = 52;

export type TopTabBarProps = MaterialTopTabBarProps & {
    headerComponent?: ReactNode;
    customEdges?: Edges;
    separatorStyles?: StyleProp<LineSeparatorStyle>;
    forceShowOneTab?: boolean;
    customWidth?: number;
};

export const TopTabBar = ({
    headerComponent,
    customEdges,
    state,
    descriptors,
    navigation,
    separatorStyles,
    forceShowOneTab = false,
    customWidth,
    ...rest
}: TopTabBarProps) => {
    const shouldShow = state.routeNames.length > 1 || forceShowOneTab;
    const { width: screenWidth } = useWindowDimensions();

    return (
        <SafeAreaView edges={customEdges || ['left', 'right', 'top']} style={[styles.container]}>
            {headerComponent}
            {shouldShow ? (
                <>
                    <View style={[common.row, common.full, common.alignCenter]}>
                        <Animated.View
                            style={[
                                common.noOverflow,
                                {
                                    width: customWidth || screenWidth,
                                },
                            ]}
                        >
                            <MaterialTopTabBar
                                {...rest}
                                state={state}
                                descriptors={descriptors}
                                navigation={navigation}
                            />
                        </Animated.View>
                    </View>
                    <LineSeparator style={[styles.separator, separatorStyles]} />
                </>
            ) : null}
        </SafeAreaView>
    );
};

export const TopTabBarLabel = (props: {
    focused: boolean;
    children: string;
    focusedColor?: AppColors;
    style?: StyleProp<TextStyle>;
    testID?: string;
}) => (
    <Text
        variant="titleMedium"
        color={props.focused ? props.focusedColor ?? ds.colorToken.text.primary : 'neutral300'}
        style={[styles.text, props.style]}
        numberOfLines={1}
        testID={props.testID}
    >
        {props.children}
    </Text>
);

export const TAB_BAR_ITEM_STYLE = {
    paddingHorizontal: 16,
    minHeight: TAB_HEIGHT,
};

const TAB_BAR_INDICATOR_STYLE = {
    backgroundColor: ds.colorValue.background.neutral.inverse,
    height: 4,
    marginBottom: -1,
};

/**
 * Default config use to style `@react-navigation/material-top-tabs`
 */
export const TOP_TAB_BAR_CONFIG: MaterialTopTabNavigationOptions = {
    // String labels render react-navigation's own Text, which scales with device text size unlike
    // the app Text (allowFontScaling=false) used by custom tabBarLabels - keep every tab consistent.
    tabBarAllowFontScaling: false,
    tabBarStyle: {
        backgroundColor: 'transparent',
        // reset shadow to avoid warning  about `shadow efficiency`
        shadowColor: 'auto',
        shadowOpacity: 0,
        shadowRadius: 0,
        elevation: 0,
        height: TAB_HEIGHT,
        paddingTop: 0.6,
        marginBottom: 1,
    },
    tabBarLabelStyle: {
        bottom: 2,
        fontSize: 15,
        lineHeight: 24,
        fontWeight: '600',
        letterSpacing: -0.23,
        fontFamily: undefined,
        textAlign: 'auto',
    },
    tabBarItemStyle: TAB_BAR_ITEM_STYLE,
    tabBarIndicatorStyle: TAB_BAR_INDICATOR_STYLE,
    tabBarPressColor: 'transparent',
    tabBarActiveTintColor: ds.colorValue.text.primary,
    tabBarInactiveTintColor: designSystem.colors.neutral300,
};

/**
 * Use this config if you want your tabs to be aligned to the left and not occupy the full width
 */
export const LEFT_ALIGNED_TAB_BAR_CONFIG: MaterialTopTabNavigationOptions = {
    ...TOP_TAB_BAR_CONFIG,
    tabBarStyle: [TOP_TAB_BAR_CONFIG.tabBarStyle, { height: 42, marginHorizontal: 16 }],
    tabBarItemStyle: {
        minHeight: 46,
        width: 'auto',
        paddingHorizontal: 0,
    },
    tabBarIndicatorStyle: {
        ...TAB_BAR_INDICATOR_STYLE,
        height: 2,
    },
    tabBarGap: 16,
};

/**
 * Use this config if you want your tabs to be 'flex' size and scrollable
 */
export const SCROLLABLE_TOP_TAB_BAR_CONFIG: MaterialTopTabNavigationOptions = {
    ...TOP_TAB_BAR_CONFIG,
    tabBarScrollEnabled: true,
    tabBarItemStyle: {
        ...TAB_BAR_ITEM_STYLE,
        width: 'auto',
    },
    tabBarIndicatorStyle: {
        ...TAB_BAR_INDICATOR_STYLE,
        marginLeft: -0.33,
    },
};

export const NEW_TOP_TAB_BAR_HEIGHT = 46;

export const NEW_TOP_TAB_BAR_CONFIG: MaterialTopTabNavigationOptions = {
    ...LEFT_ALIGNED_TAB_BAR_CONFIG,
    tabBarIndicatorStyle: {
        ...TAB_BAR_INDICATOR_STYLE,
        height: 2,
    },
    tabBarItemStyle: [LEFT_ALIGNED_TAB_BAR_CONFIG.tabBarItemStyle],
    tabBarStyle: [LEFT_ALIGNED_TAB_BAR_CONFIG.tabBarStyle, { height: NEW_TOP_TAB_BAR_HEIGHT }],
    tabBarLabelStyle: [LEFT_ALIGNED_TAB_BAR_CONFIG.tabBarLabelStyle, { alignSelf: 'center', marginTop: 3, margin: 0 }],
    tabBarActiveTintColor: ds.colorValue.text.primary,
    tabBarGap: 24,
};

const styles = StyleSheet.create({
    separator: {
        marginTop: -4,
        height: 4,
        ...common.zIndexNegative1,
    },
    container: {
        width: '100%',
        justifyContent: 'center',
    },
    text: {
        bottom: 2,
    },
});
